// Language auto-detect banner.
// On first visit, if the browser's preferred language matches one of our
// supported locales (es / pt) and the user hasn't picked a language yet,
// slide down a friendly banner offering to switch.
//
// Storage keys used:
//   - `gems_lang`                 → user's active language (set on any manual pick)
//   - `gems_lang_banner_dismissed` → set once the banner has been shown+dismissed
//                                    so we never nag the same visitor twice.

import { useEffect, useState } from 'react';
import { X, Globe, ArrowRight } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const SUPPORTED = new Set(['en', 'es', 'pt']);

// Localized banner copy — written directly in each language so the visitor
// immediately reads it in their own tongue (no key lookup needed).
const COPY = {
  es: {
    label: 'Detectamos que hablas español',
    question: '¿Cambiamos el sitio a tu idioma?',
    switch: 'Ver en español',
    stay: 'Seguir en inglés',
    close: 'Cerrar aviso de idioma',
    langName: 'Español',
  },
  pt: {
    label: 'Detectamos que você fala português',
    question: 'Quer mudar o site para o seu idioma?',
    switch: 'Ver em português',
    stay: 'Continuar em inglês',
    close: 'Fechar aviso de idioma',
    langName: 'Português',
  },
};

function detectBrowserLang() {
  if (typeof navigator === 'undefined') return null;
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || 'en'];
  for (const l of list) {
    const code = (l || '').toLowerCase().split('-')[0];
    if (SUPPORTED.has(code)) return code;
  }
  return null;
}

export default function LanguageDetectBanner() {
  const { lang, setLang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [suggested, setSuggested] = useState(null);
  // Track mount → paint so the entrance animation runs.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // If the user has already dismissed this banner in a previous session,
    // never show it again. This is a one-time nudge.
    if (localStorage.getItem('gems_lang_banner_dismissed') === '1') return;
    // If the user has already explicitly picked a language, respect that.
    if (localStorage.getItem('gems_lang')) return;

    const browser = detectBrowserLang();
    // Only surface the banner when the browser language differs from the
    // current site language AND is one we actually support (es/pt).
    if (!browser || browser === lang || browser === 'en') return;

    setSuggested(browser);
    // Small delay so the banner animates in AFTER first paint.
    const t = setTimeout(() => {
      setVisible(true);
      setMounted(true);
    }, 550);
    return () => clearTimeout(t);
  }, [lang]);

  const dismiss = () => {
    setVisible(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('gems_lang_banner_dismissed', '1');
    }
  };

  const switchLang = () => {
    if (suggested) setLang(suggested);
    dismiss();
  };

  if (!visible || !suggested) return null;
  const copy = COPY[suggested];
  if (!copy) return null;

  return (
    <div
      className={`fixed top-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-auto sm:max-w-md z-[60] transition-all duration-500 ease-out ${
        mounted ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'
      }`}
      role="dialog"
      aria-live="polite"
      aria-label="Language suggestion"
      data-testid="lang-detect-banner"
    >
      <div className="relative rounded-2xl bg-white border border-[#2A1B1A]/8 shadow-[0_28px_60px_-20px_rgba(42,27,26,0.35)] p-5 sm:p-6 overflow-hidden">
        {/* Soft accent glow */}
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#FCD968]/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-[#5DCAB5]/20 blur-3xl pointer-events-none" />

        <button
          type="button"
          onClick={dismiss}
          aria-label={copy.close}
          data-testid="lang-detect-dismiss"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#2A1B1A]/50 hover:text-[#E5614C] hover:bg-[#FAF3EE] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#E5614C]/10 text-[#E5614C] flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5" />
          </div>
          <div className="min-w-0 pr-6">
            <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[#5DCAB5]">
              {copy.langName}
            </div>
            <div className="mt-1 font-serif-display text-[19px] font-bold text-[#2A1B1A] leading-tight">
              {copy.label}
            </div>
            <p className="mt-1 text-[14px] text-[#2A1B1A]/70">{copy.question}</p>
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <button
            type="button"
            onClick={switchLang}
            data-testid="lang-detect-switch"
            className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold text-[14px] btn-press shadow-[0_10px_24px_-8px_rgba(229,97,76,0.55)]"
          >
            {copy.switch} <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="lang-detect-stay"
            className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#FAF3EE] hover:bg-[#f2e7de] text-[#2A1B1A] font-semibold text-[14px] btn-press"
          >
            {copy.stay}
          </button>
        </div>
      </div>
    </div>
  );
}
